import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pw9205Api, F9051 } from '../../../services/pw9205Api';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './usePW9205Mode';

export function usePW9205Record() {
  const [record, setRecord] = useState<F9051 | null>(null);
  const [formData, setFormData] = useState<Partial<F9051>>({});
  const [loading, setLoading] = useState(false);
  const { fieldErrors, setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by doc number */
  const loadRecord = useCallback(async (docNo: string): Promise<F9051 | null> => {
    setLoading(true);
    try {
      const data = await pw9205Api.get(docNo);
      setRecord(data);
      setFormData({ ...data });
      return data;
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback(<K extends keyof F9051>(key: K, value: F9051[K]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback(<K extends keyof F9051>(key: K): F9051[K] | string => {
    return (formData as Partial<F9051>)[key] ?? '';
  }, [formData]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: F9051) => void,
  ) => {
    setLoading(true);
    try {
      const req = { master: formData as F9051 };
      if (mode === 'add') {
        const result = await pw9205Api.create(req);
        message.success(`新增成功: ${result.f9051_01}`);
        onSuccess(result);
      } else {
        const result = await pw9205Api.update(formData.f9051_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        message.error('請修正標示的欄位');
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await pw9205Api.delete(record.f9051_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    setFormData({
      f9051_01: '',
      f9051_02: '',
      f9051_10: null,
      f9051_11: '',
      f9051_12: '',
      f9051_13: '',
      f9051_14: '',
      f9051_15: '',
      f9051_30: '',
      f9051_31: '',
      f9051_32: '',
      f9051_33: '',
      f9051_34: '',
      f9051_35: '',
      f9051_36: '',
      f9051_37: '',
      f9051_38: '',
      f9051_39: '',
      f9051_40: '',
      f9051_91: '',
      f9051_92: null,
      f9051_93: '',
    });
  }, [clearFieldErrors]);

  /** Apply result after save */
  const applyResult = useCallback((result: F9051) => {
    setRecord(result);
    setFormData({ ...result });
  }, []);

  return {
    record, formData, loading,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    fieldErrors, getFieldError, clearFieldErrors, hasErrors,
  };
}
